/**
 * Divider Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 523-1654
 *
 * A simple line separator for visual content division.
 * Supports horizontal/vertical orientation.
 * Color: Slate-100 (#ECEEF1)
 */

import React from 'react';
import { View, StyleSheet, ViewStyle } from 'react-native';
import { Colors } from '../../../../themes/colors';

export type DividerType = 'horizontal' | 'vertical';

export interface DividerProps {
  /**
   * Orientation of the divider
   * @default "horizontal"
   */
  type?: DividerType;

  /**
   * Custom length (width for horizontal, height for vertical)
   * Defaults to 100% of parent
   */
  length?: number | string;

  /**
   * Custom style overrides
   */
  style?: ViewStyle;

  /**
   * Test identifier
   */
  testID?: string;
}

const DIVIDER_THICKNESS = 1;

export const Divider: React.FC<DividerProps> = ({
  type = 'horizontal',
  length,
  style,
  testID,
}) => {
  // Read through the `Colors.role` Proxy inside the render body, not into a
  // module-scope const: on native the Proxy resolves at *read* time, so a
  // capture taken at import time freezes whatever mode was active then and
  // never updates on a theme flip.
  const dividerStyle: ViewStyle =
    type === 'horizontal'
      ? {
          height: DIVIDER_THICKNESS,
          width: length ?? '100%',
          backgroundColor: Colors.role.borderSection,
        }
      : {
          width: DIVIDER_THICKNESS,
          height: length ?? '100%',
          backgroundColor: Colors.role.borderSection,
        };

  return (
    <View
      style={[styles.divider, dividerStyle, style]}
      testID={testID}
      accessibilityRole="none"
    />
  );
};

Divider.displayName = 'Divider';

const styles = StyleSheet.create({
  divider: {
    flexShrink: 0,
  },
});
